import { createGlobalStyle } from 'styled-components';
import theme from './theme';

/**
 * globalStyles.js — element defaults, on the Vikar Experten design system.
 *
 * CHANGED in 1.17.0-dev.1.
 *
 * Note on the root font size: the website sets html{font-size:62.5%} so that
 * 1rem = 10px. We deliberately do NOT copy that — every rem in this app was
 * authored against a 16px root, and changing it would rescale the whole
 * product. Type sizes therefore come through as absolute px from the theme.
 *
 * Fonts are loaded in public/index.html (Roboto 300/400/500/700/900) and the
 * tokens are in styles/ve-tokens.css, imported from src/index.js.
 */
const GlobalStyles = createGlobalStyle`
  *{margin:0;padding:0;box-sizing:border-box}

  html{
    font-size:16px;
    scroll-behavior:smooth;
    /* Light-only design: pin the colour scheme so native controls (option
       lists, date pickers) don't render light-on-white under OS dark mode. */
    color-scheme:light;
  }

  body{
    font-family:${theme.typography.fontFamily.body};
    /* The single most recognisable typographic trait of the brand: light
       weight, generous line height. */
    font-size:${theme.typography.fontSize.base};
    font-weight:${theme.typography.fontWeight.light};
    line-height:${theme.typography.lineHeight.normal};
    color:${theme.colors.text};
    background-color:${theme.colors.offWhite};
    -webkit-font-smoothing:antialiased;
    -moz-osx-font-smoothing:grayscale;
    overflow-x:hidden;
  }

  /* Headings: bold, uppercase, tight, tracking 0. H1 36 / H2 24 / H3 22 as
     authored on the site; H4-H6 drop the uppercase and run at 1.4 — that is
     where sentence case starts. */
  h1,h2,h3,h4,h5,h6{
    font-family:${theme.typography.fontFamily.heading};
    font-weight:${theme.typography.fontWeight.bold};
    line-height:${theme.typography.lineHeight.tight};
    letter-spacing:${theme.typography.letterSpacing.normal};
    color:${theme.colors.dark};
    margin-bottom:${theme.spacing[4]};
  }
  h1{font-size:${theme.typography.fontSize['5xl']};text-transform:uppercase}
  h2{font-size:${theme.typography.fontSize['2xl']};text-transform:uppercase}
  h3{font-size:${theme.typography.fontSize.xl};text-transform:uppercase}
  h4{font-size:${theme.typography.fontSize.lg};line-height:${theme.typography.lineHeight.snug}}
  h5{font-size:${theme.typography.fontSize.md};line-height:${theme.typography.lineHeight.snug}}
  h6{font-size:${theme.typography.fontSize.base};line-height:${theme.typography.lineHeight.snug}}

  p{margin-bottom:${theme.spacing[4]}}

  /* Links are actions, so they take the action blue. The brand's red underlined
     link is kept only where red still means the brand: never next to a reject
     button, where a red link would read as destructive. */
  a{
    color:${theme.colors.primary};
    text-decoration:underline;
    transition:${theme.transitions.default};
  }
  a:hover{color:${theme.colors.text};text-decoration:none}

  ul,ol{margin-bottom:${theme.spacing[4]};padding-left:${theme.spacing[6]}}
  li{margin-bottom:${theme.spacing[2]}}

  /* The brand's bullet is a red chevron, not a disc. Scoped to prose blocks so
     it never leaks into navigation or control lists. */
  .prose ul{list-style:none;padding-left:${theme.spacing[5]}}
  .prose li{position:relative;padding-left:${theme.spacing[4]}}
  .prose li::before{
    content:"\\f054";
    font-family:"Font Awesome 5 Free";
    font-weight:900;
    font-size:9px;
    color:${theme.colors.brandRed};
    position:absolute;left:0;top:7px;
  }

  img{max-width:100%;height:auto}

  button,input,select,textarea{font-family:${theme.typography.fontFamily.body}}

  /* NOTE: this app styles every bare <button> as the primary action (a
     pre-existing pattern — see AdminLayout.css, which has to undo it for menu
     items). Kept as-is to avoid a behavioural change, but moved onto the action
     blue: hover darkens the same fill and adds the pressed inset. Green is NOT
     the universal hover any more — it means approve. */
  button{
    cursor:pointer;
    background-color:${theme.components.button.primary.backgroundColor};
    color:${theme.components.button.primary.color};
    border:none;
    border-radius:${theme.components.button.primary.borderRadius};
    padding:${theme.spacing[3]} ${theme.spacing[6]};
    font-weight:${theme.typography.fontWeight.bold};
    transition:${theme.transitions.default};
  }
  button:hover{
    background-color:${theme.components.button.primary.hoverBackgroundColor};
    color:${theme.components.button.primary.hoverColor};
    box-shadow:${theme.shadows.press};
  }
  /* The source has no focus state at all. This is a required modernisation. */
  button:focus-visible{outline:none;box-shadow:${theme.shadows.focus}}
  button:disabled{opacity:.6;cursor:not-allowed;box-shadow:none}
  button:disabled:hover{background-color:${theme.components.button.primary.backgroundColor};color:${theme.components.button.primary.color}}

  input,select,textarea{
    width:100%;
    color:${theme.colors.text};
    background-color:${theme.components.form.input.backgroundColor};
    border:1px solid ${theme.components.form.input.borderColor};
    border-radius:${theme.components.form.input.borderRadius};
    padding:${theme.components.form.input.padding};
    font-size:${theme.components.form.input.fontSize};
    font-weight:${theme.typography.fontWeight.light};
    transition:${theme.transitions.default};
  }
  input:focus,select:focus,textarea:focus{
    outline:none;
    border-color:${theme.components.form.input.focusBorderColor};
    box-shadow:${theme.components.form.input.focusRing};
  }
  input::placeholder,textarea::placeholder{color:${theme.colors.brand.ink400};opacity:1}

  /* The width:100% above is for text-like fields. A checkbox/radio stretched
     to 100% hijacks its row's flex layout (the Integrations scope/event rows
     rendered their code chips one letter per line) — keep them intrinsic. */
  input[type="checkbox"],input[type="radio"]{width:auto;padding:0}

  /* Explicit dark option text so dropdown items are never white-on-white. */
  option{color:${theme.colors.text};background-color:#ffffff}

  label{
    display:block;
    color:${theme.components.form.label.color};
    font-size:${theme.components.form.label.fontSize};
    font-weight:${theme.components.form.label.fontWeight};
    margin-bottom:${theme.components.form.label.marginBottom};
  }

  .container{
    width:100%;
    max-width:${theme.grid.containerWidth.xxl};
    margin-left:auto;margin-right:auto;
    padding-left:${theme.spacing[4]};padding-right:${theme.spacing[4]};
    @media (min-width:1024px){padding-left:${theme.spacing[6]};padding-right:${theme.spacing[6]}}
  }

  /* Cards are white, square, hairline-bordered and flat at rest. */
  .card{
    background-color:${theme.components.card.backgroundColor};
    border:${theme.components.card.border};
    border-radius:${theme.components.card.borderRadius};
    padding:${theme.components.card.padding};
    box-shadow:${theme.components.card.boxShadow};
  }

  section{padding:${theme.spacing[8]} 0}

  /* A 7px red rule under a headline — the only ornament in the brand. It stays
     brand red: this is identity, not an action. */
  .splitter{height:7px;width:64px;background:${theme.colors.brandRed};margin:${theme.spacing[4]} 0}

  .text-center{text-align:center}
  .text-right{text-align:right}
  .mb-0{margin-bottom:0}
  .mt-0{margin-top:0}

  .text-success{color:${theme.colors.success}}
  .text-warning{color:${theme.colors.warning}}
  .text-error{color:${theme.colors.error}}
  .text-info{color:${theme.colors.info}}
  .bg-light{background-color:${theme.colors.light}}
  .bg-white{background-color:${theme.colors.white}}
`;

export default GlobalStyles;
